import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import type { Dish } from "../types/dishes.types";

/**
 * A macro as a cell prints it.
 *
 * At most one decimal, and none at all where the figure is whole: the numbers
 * that come back are a mix of round hundreds and small fractions, and 250
 * should read as "250" while 0.2 has to stay 0.2 rather than being rounded to
 * a zero that says the dish has no fat in it.
 */
export const formatMacro = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/**
 * The row's place in the list, as a two-digit number.
 *
 * Counted from where the page starts rather than from the top of the table, so
 * page two opens at 11 instead of starting over at 01 — the column is the
 * dish's position in the whole list, not on the screen. Padded to two digits
 * because that is what keeps the column a column; past 99 it simply grows.
 */
const serial = (index: number) => String(index).padStart(2, "0");

const COLUMNS = ["Sl", "Dish name", "Cal.", "Carb", "Prot.", "Fat."];

interface DishesTableProps {
  dishes: Dish[];
  /**
   * The list position of the first row — `(page - 1) * pageSize + 1`. Worked
   * out by the screen, which is the one that knows which page it asked for.
   */
  startIndex: number;
  /** Stands in for the table body when the list comes back with no rows. */
  empty: EmptyStateProps;
  /**
   * Where the list is. While it is on its way the body says nothing — no rows
   * and no message — and only a load slow enough to notice shimmers, as one
   * block the size of the table. Rows already in hand are left alone either
   * way: a table with something to show goes on showing it while the next
   * answer is fetched.
   */
  phase?: LoadPhase;
}

const DishesTable: React.FC<DishesTableProps> = ({
  dishes,
  startIndex,
  empty,
  phase = "ready",
}) =>
  phase !== "ready" && dishes.length === 0 ? (
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
    // The only scroll region on the page: rows move, the sticky header row and
    // the card's pagination footer stay put.
    <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
      {/* Full height only when the rows are gone: an empty table that stops at
          its header leaves the state stranded at the top of the card with a
          screenful of nothing under it, so the body takes the whole scroll
          region and the one cell in it centres in that. With rows to show the
          table sizes to them, or every row would stretch to share the height. */}
      <table
        className={`w-full min-w-[720px] table-fixed border-collapse text-left ${
          dishes.length === 0 ? "h-full" : ""
        }`}
      >
        <colgroup>
          <col className="w-[7%]" />
          <col className="w-[37%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr>
            {COLUMNS.map((heading) => (
              <th
                key={heading}
                scope="col"
                className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        {dishes.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length} className="align-middle">
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {dishes.map((dish, index) => (
              // No hover tint and no cursor: nothing opens from this table, and
              // a row that lights under the pointer promises a screen behind it
              // that does not exist.
              <tr key={dish.id}>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink-muted tabular-nums">
                  {serial(startIndex + index)}
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  <span className="block truncate" title={dish.name}>
                    {dish.name}
                  </span>
                </td>
                {/* The four figures are per the dish's base quantity — 100 gm,
                    as a rule — not per serving. The heading has no room to say
                    so, so each cell carries it as a tooltip rather than the
                    column repeating it twenty times down the page. */}
                {[dish.macros.calories, dish.macros.carbs, dish.macros.protein, dish.macros.fat].map(
                  (value, position) => (
                    <td
                      key={COLUMNS[position + 2]}
                      className="px-4 sm:px-6 py-4 text-[14px] text-ink tabular-nums"
                      title={`Per ${formatMacro(dish.baseQuantity)} ${dish.baseUnit}`}
                    >
                      {formatMacro(value)}
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );

export default DishesTable;
